import { WizardStepErrors } from "@components/WizardCst/types";
import { EditionKind, EditionSession } from "../types";

export type MeetingTool = "MSTEAMS" | "GMEET" | "OTHER";

export interface PersonRef {
  id: string | number;
  label: string;
}

export interface BaseSessionDraft {
  id?: number | string;
  start_date: string | null;
  end_date: string | null;
  faculties: PersonRef[];
  tutors: PersonRef[];
}

export interface VirtualSessionDraft extends BaseSessionDraft {
  tool: MeetingTool | null;

  producer: PersonRef[];
  presenter: PersonRef[];
  coorganizer: PersonRef[];
  is_dial_in_bypass_enabled: boolean;
  scope: string | null;
  allow_meeting_chat: string | null;
  allow_attendee_to_enable_camera: boolean;
  allow_attendee_to_enable_mic: boolean;
  allow_breakout_rooms: boolean;
  allow_recording: boolean;
  allow_transcription: boolean;
  record_automatically: boolean;
  allowed_presenters: string | null;

  cohost: PersonRef[];
  access_type: string | null;
  chat_restriction: string | null;
  default_join_as_viewer_type: boolean;
  auto_transcription_generation: boolean;
  auto_recording_generation: boolean;

  link: string | null;

  confirmed: boolean;
  trigger_external_services: boolean;
  online_meeting_updated_at?: number | null;
}

export interface PhysicalSessionDraft extends BaseSessionDraft {
  site: PersonRef | null;
  rooms: PersonRef[];
  qrCode: boolean;
  confirmed: boolean;
}

export type SessionDraft = VirtualSessionDraft | PhysicalSessionDraft;

export const createEmptyVirtualSessionDraft = (): VirtualSessionDraft => ({
  start_date: null,
  end_date: null,
  faculties: [],
  tutors: [],
  tool: null,
  producer: [],
  presenter: [],
  coorganizer: [],
  is_dial_in_bypass_enabled: false,
  scope: null,
  allow_meeting_chat: null,
  allow_attendee_to_enable_camera: false,
  allow_attendee_to_enable_mic: false,
  allow_breakout_rooms: false,
  allow_recording: false,
  allow_transcription: false,
  record_automatically: false,
  allowed_presenters: null,
  cohost: [],
  access_type: null,
  chat_restriction: null,
  default_join_as_viewer_type: false,
  auto_transcription_generation: false,
  auto_recording_generation: false,
  link: null,
  confirmed: false,
  trigger_external_services: false,
});

export const createEmptyPhysicalSessionDraft = (): PhysicalSessionDraft => ({
  start_date: null,
  end_date: null,
  faculties: [],
  tutors: [],
  site: null,
  rooms: [],
  qrCode: false,
  confirmed: false,
});

export interface SessionWizardTriggerPayload {
  kind: EditionKind;
  corporateId: string | number;
  editionId?: string | number;
  contextLabel?: string;
  editSession?: EditionSession | SessionDraft;
  publishDate?: string | number | null;
  otherSessions?: SessionDraft[];
  onConfirm: (draft: SessionDraft) => void | boolean | Promise<void | boolean>;
}

export interface SessionStepProps<T> {
  data: T;
  setData: (patch: Partial<T>) => void;
  errors: WizardStepErrors;
  corporateId: string | number;
  disabledFields: Set<string>;
  confirmedDisabledReason?: string;
  datesDisabledReason?: string;
  startMinDate?: string | number | null;
}
